import { user } from 'src/StateObserver';
import i18n from 'src/lib/i18n/i18n';
import { captureGenericError } from 'src/lib/sentry';
import { withLoading } from 'src/lib/utils';
import {
  canAddFriends,
  isFriendshipRequestedSent,
} from 'src/replicant/friends/friends.getters';
import {
  PlayerEntryData,
  PlayerProfile,
} from 'src/replicant/friends/friends.ruleset';
import { FriendListPlayerParams } from '../components/FriendListItem';
import { sendAddFriendRequest } from '../friends.sequences';
import { fetchClusterSuggestions } from './friendSuggestions.utils';
import {
  FriendSuggestionsListItem,
  SuggestionsDividerParams,
  SuggestionsHeaderParams,
} from './friendSuggestions.items';

type SuggestionsTab = 'friend_list' | 'friend_request';

type ClusterSuggestionParams = {
  items: FriendListPlayerParams[];
};

/**
 * Shared "You may know" suggestions behaviour used by both the My Friends and
 * Requests tabs of the Friend List popup (#11174).
 *
 * Owns the per-popup-open state that must NOT reset on every `refresh()` (which
 * can run several times per open): the set of tiles already sent (so a re-tap is
 * a no-op — one `FriendRequestSend` per tile per open). Call `reset()` from the
 * popup's `init()` so a fresh open starts clean.
 */
export class FriendSuggestionsSection {
  private sentIds = new Set<string>();

  constructor(
    private readonly tab: SuggestionsTab,
    private readonly onRefresh: () => void,
  ) {}

  /** Reset per-open state. Call once per popup open. */
  public reset() {
    this.sentIds.clear();
  }

  /** Header row params for the ribbon banner. */
  private headerParams(): SuggestionsHeaderParams {
    return {
      isSuggestionsHeader: true,
      label: i18n('friendsList.youMayKnow'),
    };
  }

  /** Divider row params shown above the ribbon header. */
  private dividerParams(): SuggestionsDividerParams {
    return { isSuggestionsDivider: true };
  }

  /**
   * Assemble the combined scroll list shared by both Friend List tabs: the
   * existing rows (friends / requests), then — when there are rows above it —
   * a divider, the "You may know" ribbon header, and the suggestion tiles.
   * Mirrors the invite-list "rebuild a combined array, then setItems(_, false)"
   * idiom so both tabs render the section identically.
   */
  public buildRows(
    rows: FriendListPlayerParams[],
    suggestions: FriendListPlayerParams[],
  ): FriendSuggestionsListItem[] {
    return [
      ...rows,
      ...(rows.length > 0 ? [this.dividerParams()] : []),
      this.headerParams(),
      ...suggestions,
    ];
  }

  /**
   * Fetch the cluster suggestions and map them to friend-tile params.
   *
   * My Friends tab: every candidate renders; a tile the user has already sent a
   * request to shows the "sent / pending" state (see `mapSuggestionToParams`).
   * Requests tab: a sent request is instead shown in the requests list above, so
   * such candidates are dropped here to avoid showing the same person twice.
   *
   * Failures degrade to an empty section (reported to Sentry) so the caller can
   * fire-and-forget without an unhandled rejection.
   */
  public async fetchParams(): Promise<ClusterSuggestionParams> {
    // Over the friend cap the server rejects every send (#12030). Hide the
    // section, like every other add-friend surface hides its button.
    if (!canAddFriends(user())) return { items: [] };

    try {
      const { suggestions } = await fetchClusterSuggestions().get();

      // On the Requests tab a sent request already appears in the requests list
      // above, so drop already-sent suggestions to avoid a duplicate tile.
      const visible =
        this.tab === 'friend_request'
          ? suggestions.filter(
              (entry) =>
                !this.sentIds.has(entry.id) &&
                !isFriendshipRequestedSent(user(), entry.id),
            )
          : suggestions;

      const items = visible.map((entry) => this.mapSuggestionToParams(entry));

      return { items };
    } catch (error) {
      captureGenericError('Failed to fetch cluster friend suggestions', error);
      return { items: [] };
    }
  }

  private mapSuggestionToParams(
    entry: PlayerEntryData,
  ): FriendListPlayerParams {
    const player: PlayerProfile = {
      id: entry.id,
      name: entry.name,
      profilePicture: entry.photo,
      frame: entry.avatarFrame,
      species: entry.petSpecies,
    };
    // A tile shows the "Requested" pill once a request has been sent to it —
    // either this open (sentIds) or already pending in state — instead of the
    // Add button (spec: the tile transitions to sent/pending after a send).
    const requestSent =
      this.sentIds.has(entry.id) || isFriendshipRequestedSent(user(), entry.id);
    return {
      ...player,
      requestSent,
      // Close over the entry so we can read its originSubFeature at tap time.
      onAdd: requestSent
        ? undefined
        : () => this.handleAdd(player, entry.originSubFeature),
    };
  }

  /**
   * Direct-send a friend request from a suggestion tile (no confirm popup —
   * the "Add" button is the confirmation). Debounced per tile per open. On
   * success the list is refreshed and the tile re-renders in the "sent /
   * pending" state (the "Requested" pill) — per spec it transitions to sent
   * rather than disappearing. The tile stays in the cached section; a later
   * fresh server fetch drops it (the new pending request is in `excludeIds`).
   */
  private async handleAdd(
    player: PlayerProfile,
    originSubFeature?: 'friend_list_cluster' | 'friend_list_suggestions',
  ): Promise<void> {
    // Ignore re-taps on a tile we already sent this open.
    if (this.sentIds.has(player.id)) return;
    this.sentIds.add(player.id);

    // Blocking loader while the request sends: gives "processing" feedback and
    // serializes rapid Adds so a second tap can't race the re-render below
    // (replaces the per-tab refresh generation guard).
    const sent = await withLoading(() =>
      sendAddFriendRequest(player, {
        sourceFeature: 'suggestions',
        originFeature: 'friend_list',
        originSubFeature: originSubFeature ?? 'friend_list_suggestions',
        skipConfirmation: true,
      }),
    );

    if (!sent) {
      // Allow a retry if the send did not go through.
      this.sentIds.delete(player.id);
      return;
    }

    // Keep the tile in place; refresh so it re-renders as "sent / pending".
    this.onRefresh();
  }
}
